import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API_BASE = `${BACKEND_URL}/api`;

export const api = axios.create({
  baseURL: API_BASE,
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem("jb_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (res) => res,
  (err) => {
    // Phase P.1 — only wipe jb_token when the failing endpoint is a
    // dedicated session-validation route. A 401 on any random endpoint
    // (permission checks, discount lookups, stale cached URLs) must NOT
    // destroy the user's active session. Previously ANY 401 wiped the
    // token and redirected to /login, which took out /billing whenever
    // a downstream call happened to 401.
    const failedUrl = err?.config?.url || "";
    const isSessionCheck =
      failedUrl.endsWith("/auth/me") ||
      failedUrl.endsWith("/auth/refresh");
    if (err?.response?.status === 401 && isSessionCheck) {
      if (localStorage.getItem("jb_token")) {
        localStorage.removeItem("jb_token");
        if (window.location.pathname !== "/login") {
          window.location.assign("/login");
        }
      }
    }
    // Phase 12: subscription-frozen → bounce to /frozen unless already there.
    if (err?.response?.status === 402 && err?.response?.data?.subscription_status === "frozen") {
      if (window.location.pathname !== "/frozen") {
        window.location.assign("/frozen");
      }
    }
    // Phase 16: trial-expired (read-only) → bounce to /billing.
    if (err?.response?.status === 402 && err?.response?.data?.subscription_status === "trial_read_only") {
      if (window.location.pathname !== "/billing") {
        window.location.assign("/billing");
      }
    }
    return Promise.reject(err);
  }
);

// Phase 11: separate axios instance for platform (super-admin) endpoints.
export const platformApi = axios.create({ baseURL: API_BASE });
platformApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("jb_platform_token");
  if (token) {
    config.headers = config.headers || {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});
platformApi.interceptors.response.use(
  (res) => res,
  (err) => {
    if (err?.response?.status === 401) {
      if (localStorage.getItem("jb_platform_token")) {
        localStorage.removeItem("jb_platform_token");
        if (window.location.pathname !== "/admin/login") {
          window.location.assign("/admin/login");
        }
      }
    }
    return Promise.reject(err);
  }
);

export function formatApiError(detail) {
  // Phase 46 — delegated to `lib/apiErrors.js` so it can be unit-tested
  // in isolation (the axios import would otherwise pull in ESM that
  // CRA's jest config doesn't transform). Kept here as a thin re-export
  // to preserve every existing import path.
  // eslint-disable-next-line global-require
  const { formatApiError: impl } = require("./apiErrors");
  return impl(detail);
}

export function extractFieldErrors(err) {
  // Phase 47 — sibling helper for inline per-field 422 surfacing.
  // eslint-disable-next-line global-require
  const { extractFieldErrors: impl } = require("./apiErrors");
  return impl(err);
}

export function buildAssetUrl(path) {
  if (!path) return "";
  if (/^https?:\/\//.test(path)) return path;
  return `${BACKEND_URL}${path}`;
}

export const SUPPORTED_CURRENCIES = [
  { code: "EUR", label: "Euro (€)" },
  { code: "GBP", label: "British Pound (£)" },
  { code: "USD", label: "US Dollar ($)" },
  { code: "CAD", label: "Canadian Dollar (C$)" },
  { code: "AUD", label: "Australian Dollar (A$)" },
  { code: "NZD", label: "New Zealand Dollar (NZ$)" },
  { code: "CHF", label: "Swiss Franc (CHF)" },
  { code: "SEK", label: "Swedish Krona (kr)" },
  { code: "NOK", label: "Norwegian Krone (kr)" },
  { code: "DKK", label: "Danish Krone (kr)" },
];

export function formatCurrency(amount, currencyCode = "EUR") {
  const value = Number.isFinite(Number(amount)) ? Number(amount) : 0;
  try {
    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: currencyCode || "EUR",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  } catch {
    return `${currencyCode} ${value.toFixed(2)}`;
  }
}
